import type { ReactElement } from 'react';
import { Panel, PanelHeader, Skeleton } from '@/components/ui';
import { cx } from '@/lib/cx';
import { formatDecimalString } from '@/lib/format';
import { compactMoneyParts } from '@/lib/targets';
import { CompactFigure } from '@/components/dashboards/TargetTiles';
import type { RevenueCurrency } from '@/lib/use-revenue-analytics';
import { ALL, peakOf, type CycleTotal } from './revenue-company-shared';

/**
 * WHAT EACH CYCLE CONTRIBUTED — and the cycle picker, which is the same control.
 *
 * 🔴 **IT LIVES IN `revenue-company/` AND THE COMPANY TAB NO LONGER MOUNTS IT —
 * 13 September 2026, owner's instruction to remove the "Revenue by cycle" card
 * from Revenue by Company.** Its ONE remaining caller is
 * `revenue-product/RevenueByProductPanel.tsx`.
 *
 * ⛔ **IT WAS DELIBERATELY NOT DELETED WITH THAT CARD.** The removal was scoped
 * to one tab; deleting the component would have taken the cycle strip off the
 * PRODUCT tab as well, which nobody asked for. **Do not delete it as an orphan —
 * check `RevenueByProductPanel` first, because a grep of this folder alone now
 * finds no mount and reports a live component as dead.**
 *
 * ⚠️ **AND THE REVERSE HAZARD IS NOW LIVE: a component mounted for ONE caller
 * disappears with that caller.** If Revenue by Product ever drops its strip, this
 * file really is orphaned and nothing will fail to say so — `tsc` cannot tell a
 * finished module from an unreferenced one. Whoever removes that mount owns this
 * file too.
 *
 * ⚠️ **WHERE IT SITS IS NOW WRONG AND IS NOT BEING FIXED HERE.** A component
 * whose only caller is the sibling folder belongs in neither — it belongs one
 * level up, beside `TargetTiles`. Moving it touches `revenue-product/`, which is
 * another owner's, so it is recorded rather than done.
 *
 * 🔴 **THE ROWS ARE BUTTONS THAT SET THE CYCLE FILTER.** That is the Accounts
 * Dashboard's stat-card pattern: a summary figure that is also the way into the
 * thing it summarises, so a reader who has just seen that 2025-26 is the big
 * year does not then have to find that year again in a `<select>`. The `<select>`
 * in the filter bar sets the same state — two controls, one piece of state,
 * exactly as a stat card and its filter panel already do.
 *
 * 🔴 **THE FIGURES COVER EVERY CYCLE AND ARE NEVER NARROWED BY THE CYCLE
 * SELECTION.** They are narrowed by the currency, the band and the search, and
 * by nothing else. Narrowing them by their own selection would leave one bar on
 * screen — the reader would lose the comparison by touching the control that
 * exists to move through it. Same rule the stat cards follow: the row stays
 * whole when one of its cards is pressed.
 *
 * ⚠️ **A CYCLE WITH NOTHING IN THIS CURRENCY DRAWS NO BAR AND SHOWS AN EM
 * DASH.** Not a zero-width bar beside `0.00` — that is a claim that the
 * business traded nothing, where the truth is usually that it traded in another
 * currency. The row stays clickable regardless: an empty cycle is a legitimate
 * thing to look at, and a control that appears and disappears as the currency
 * changes reads as a broken page.
 *
 * ⚠️ **PRESSING THE SELECTED CYCLE CLEARS IT.** An `aria-pressed` button is a
 * toggle and readers treat it as one, so pressing the current selection returns
 * to every cycle rather than doing nothing — a control that visibly does not
 * respond is the thing this codebase names *a call that succeeds and does
 * nothing*.
 */

/**
 * The bar.
 *
 * ⚠️ **`aria-hidden`, AND THE FIGURE BESIDE IT IS THE REAL ANSWER.** A bar is a
 * comparison drawn in width; a screen reader already gets the exact amount from
 * the cell next to it, and announcing a decorative div as well would say the
 * same fact twice in a vaguer form.
 *
 * ⚠️ **Width comes from `Number()` and is GEOMETRY, never a figure.** The
 * amount rendered beside it is the exact decimal string it arrived as. Clamped
 * at 100 because a bar cannot be wider than its track — the *number* is never
 * clamped anywhere on this tab.
 */
function CycleBar({ amount, peak }: { amount: string | null; peak: number }): ReactElement {
  const value = amount === null ? 0 : Number(amount);
  const width =
    peak <= 0 || !Number.isFinite(value) || value <= 0
      ? 0
      : Math.min(100, (value / peak) * 100);

  return (
    <div
      aria-hidden="true"
      className="h-1.5 min-w-8 flex-1 overflow-hidden rounded-xs bg-surface-sunken"
    >
      <div className="h-full rounded-xs bg-chart-1" style={{ width: `${width}%` }} />
    </div>
  );
}

export function RevenueCycleStrip({
  totals,
  currency,
  selected,
  onSelect,
  loading,
  population = 'every cycle',
}: {
  /** Every cycle, in date order, with the figure for the companies on screen. */
  totals: readonly CycleTotal[];
  currency: RevenueCurrency;
  /**
   * WHAT the figures in this strip are the revenue OF, in a few words.
   *
   * 🔴 *SUPERSEDED 2026-09-13, the premise and not the prop: "**TWO TABS MOUNT
   * THIS COMPONENT AND THEY MEAN DIFFERENT THINGS.** On Revenue by Company these
   * bars are companies; on Revenue by Product they are products … a reader moving
   * between tabs had nothing on screen telling them the panel had changed
   * meaning."* **One tab mounts it now** — the company card was removed — so the
   * ambiguity this prop was added to resolve cannot currently arise.
   *
   * ⛔ **IT IS STILL REQUIRED READING AND THE PROP STAYS.** The only caller passes
   * `"every cycle · products on screen"`, and the heading above it is the
   * hardcoded *"Revenue by cycle"* — so **this prop is the only thing on screen
   * saying these bars are PRODUCTS' money rather than companies'.** Dropping it
   * for being "the same on every caller" would leave a panel that reads as a
   * company figure on a product tab.
   *
   * ⚠️ **It describes the POPULATION, not the axis.** What separates two callers
   * is whose revenue is being cut by cycle, not the cycle.
   *
   * ⚠️ **THE DEFAULT IS NOW UNREACHABLE AND IS KEPT DELIBERATELY.** `'every
   * cycle'` existed so an un-migrated caller read exactly as it had; no caller
   * omits the prop today. **Do not make it required on the strength of that** — a
   * default that says less is a safe fallback, where a build error on the next
   * caller is not, and this component has just lost one caller already.
   */
  population?: string;
  /** The chosen cycle's name, or {@link ALL}. */
  selected: string;
  onSelect: (cycleName: string) => void;
  loading: boolean;
}): ReactElement {
  const peak = peakOf(totals);

  return (
    <Panel className="overflow-hidden">
      <PanelHeader
        title="Revenue by cycle"
        /*
         * ⚠️ The population belongs in the DESCRIPTION rather than the title.
         * Both tabs are answering "revenue by cycle" — the title is right on
         * each — and it is the qualifier underneath that says whose.
         */
        /*
         * The unit and the population, in the heading rather than under it.
         * Every figure in this panel is one currency and paid-only, and a
         * heading that does not say so is a heading somebody quotes without the
         * qualifier.
         */
        description={`Paid, incl. tax · ${currency} · ${population}`}
      />

      {loading ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          {/*
           * Four placeholder rows rather than one: the panel is a list, and a
           * single bar reads as a panel that has finished loading with one
           * cycle in it.
           */}
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-6 w-full" />
          ))}
        </div>
      ) : (
        <ul aria-label="Revenue by cycle" className="m-0 flex list-none flex-col p-0">
          {totals.map((entry) => {
            const isSelected = selected === entry.cycle.name;

            return (
              <li key={entry.cycle.id} className="flex border-b border-border last:border-b-0">
                <button
                  type="button"
                  aria-pressed={isSelected}
                  /*
                   * Pressing the selected cycle clears back to every cycle —
                   * see the toggle note in this file's header.
                   */
                  onClick={() => onSelect(isSelected ? ALL : entry.cycle.name)}
                  className={cx(
                    'relative flex min-w-0 flex-1 items-center gap-3 px-4 py-2 text-left',
                    'transition-colors duration-150 ease-out motion-reduce:transition-none',
                    'focus-visible:-outline-offset-2',
                    isSelected
                      ? 'bg-surface-active'
                      : 'bg-surface hover:bg-surface-hover active:bg-surface-active',
                  )}
                >
                  {/*
                   * ⚠️ **Selection is marked by a BAR as well as a background
                   * step**, so it survives for a reader who cannot separate the
                   * two surface tones — a mark that is present or absent is a
                   * shape difference, not a hue one. `aria-pressed` is the third
                   * signal and the one assistive tech reads. Absolutely
                   * positioned rather than a border, so selecting does not
                   * resize the row and shunt the list.
                   */}
                  {isSelected && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 w-0.5 bg-fg"
                    />
                  )}

                  <span
                    className={cx(
                      'w-[92px] shrink-0 text-xs whitespace-nowrap',
                      isSelected ? 'font-semibold text-fg' : 'font-medium text-fg-secondary',
                    )}
                  >
                    {entry.cycle.name}
                  </span>

                  <CycleBar amount={entry.amount} peak={peak} />

                  <span className="flex w-[132px] shrink-0 items-baseline justify-end gap-1 whitespace-nowrap">
                    {entry.amount === null ? (
                      /*
                       * ⚠️ An em dash, never `0.00`. See this file's header —
                       * "nothing in this currency" is not "nothing was sold".
                       */
                      <span className="text-xs text-fg-subtle">—</span>
                    ) : (
                      <>
                        {/* No code span: it read `USD US$468.13K`. See `Money`
                            in `RevenueCompanyTable.tsx`. */}
                        {/*
                         * ⚠️ **COMPACTED, WITH THE EXACT DECIMAL ON THE `title`.**
                         * `1.20M` has thrown away everything below the hundred
                         * thousand, so two cycles £40,000 apart read identically
                         * — and this strip is a COMPARISON, which is the one
                         * place that matters most. The `title` is the only
                         * remaining copy of the precise number; the BAR beside
                         * it is still sized from the exact string, never from
                         * anything rendered here.
                         *
                         * ⚠️ The `title` sits on the DIGITS rather than on the
                         * `w-[132px]` wrapper, because that wrapper is shared
                         * with the em-dash branch and a "—" must never carry a
                         * figure.
                         *
                         * ⚠️ Only the `K`/`M` is gold — `CompactFigure` owns that
                         * boundary and the no-whitespace rule that keeps the
                         * three pieces one figure.
                         */}
                        <span
                          className="text-xs font-medium text-fg tabular"
                          title={`${formatDecimalString(entry.amount)} ${currency}`}
                        >
                          <CompactFigure parts={compactMoneyParts(entry.amount, currency)} />
                        </span>
                      </>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
